import React, { useState, useEffect, useCallback } from "react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  CheckIcon,
} from "lucide-react";
import api from "../services/api";

interface RoleData {
  id: string;
  name: string;
  description: string | null;
  is_system_role: boolean;
  permissions: string[];
  user_count: number;
  created_at: string;
}

interface PermDef {
  resource: string;
  actions: string[];
  category: string;
}

export const Roles: React.FC = () => {
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [permDefs, setPermDefs] = useState<PermDef[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPermissions, setFormPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([api.getRoles(), api.getPermissions()]);
      setRoles(r);
      setPermDefs(p);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const togglePermission = (perm: string) => {
    setFormPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  };

  const openCreate = () => {
    setIsEdit(false);
    setFormName("");
    setFormDescription("");
    setFormPermissions(new Set());
    setShowModal(true);
  };

  const openEdit = (role: RoleData) => {
    setIsEdit(true);
    setSelectedRole(role);
    setFormName(role.name);
    setFormDescription(role.description || "");
    setFormPermissions(new Set(role.permissions));
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const perms = Array.from(formPermissions);
      if (isEdit && selectedRole) {
        await api.updateRole(selectedRole.id, {
          name: formName,
          description: formDescription,
          permissions: perms,
        });
      } else {
        await api.createRole({
          name: formName,
          description: formDescription,
          permissions: perms,
        });
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save role");
    }
  };

  const handleDelete = async (role: RoleData) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.deleteRole(role.id);
      if (selectedRole?.id === role.id) setSelectedRole(null);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to delete role");
    }
  };

  // Group permissions by category
  const permsByCategory: Record<string, PermDef[]> = {};
  permDefs.forEach((pd) => {
    if (!permsByCategory[pd.category]) permsByCategory[pd.category] = [];
    permsByCategory[pd.category].push(pd);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading roles...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Roles
        </h1>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Role
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roles.map((role) => (
          <div
            key={role.id}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow p-5 border border-gray-200 dark:border-gray-700 cursor-pointer transition-colors ${
              selectedRole?.id === role.id
                ? "ring-2 ring-blue-500"
                : "hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={() => setSelectedRole(role)}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {role.name}
                </h3>
                {role.is_system_role && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                    System
                  </span>
                )}
              </div>
              {!role.is_system_role && (
                <div className="flex space-x-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(role);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(role);
                    }}
                    className="p-1 text-red-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2">
              {role.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">
                  {role.description}
                </p>
              )}
              <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                {role.user_count} {role.user_count === 1 ? "user" : "users"}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {role.permissions.slice(0, 6).map((p) => (
                <span
                  key={p}
                  className="text-xs px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded"
                >
                  {p}
                </span>
              ))}
              {role.permissions.length > 6 && (
                <span className="text-xs text-gray-400">
                  +{role.permissions.length - 6} more
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {isEdit ? "Edit Role" : "Create Role"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Permissions
                </label>
                {Object.entries(permsByCategory).map(([category, defs]) => (
                  <div key={category} className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      {category}
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {defs.flatMap((def) =>
                        def.actions.map((action) => {
                          const perm = `${def.resource}:${action}`;
                          const active = formPermissions.has(perm);
                          return (
                            <button
                              key={perm}
                              type="button"
                              onClick={() => togglePermission(perm)}
                              className={`flex items-center px-2 py-1.5 text-xs rounded-md border transition-colors ${
                                active
                                  ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300"
                                  : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                              }`}
                            >
                              {active && (
                                <CheckIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                              )}
                              <span className="truncate">{perm}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formName.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isEdit ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
